import React, {useEffect, useRef} from "react"

import {useDispatch, useSelector} from "react-redux"
import {Group, Layer, Stage} from "react-konva"

import {AutoResizeDiv} from "../AutoResizeDiv"
import {createGroundViewLayout} from "./layoutFunctions"
import {getLidSize, getCombinedLidSize} from "../../domain/lid"
import {getCombinedSize, getOverallSize} from "../../domain/composition"
import {createDimensioning, createMeasurementStopChainFromPositions, toWithOverallMeasurement} from "../dimensioning"
import {getVaultSize} from "../../domain/vault"
import {planImageActions} from "../../../actions/planImageActions"
import {getGroundViewBottomMeasurementPositions, getGroundViewLeftMeasurementPositions} from "./lidDimensioning";
import {getIntermediateCeilingSize} from "../../domain/intermediateCeiling";
import {LidGroundView} from "./Lid/GroundView";
import {setDirtyFalse} from "../../../features/plan/planDirtyReducer";


const GroundView = props => {
    const {clientSize} = props
    const plan = useSelector(state => state.plan)
    const dispatch = useDispatch()
    const theStage = useRef(null)

    useEffect(() => {
            if (!theStage.current) {
                return
            }
            dispatch(planImageActions.setLidGroundViewImage(theStage.current.toDataURL({pixelRatio: 2})))
            dispatch(setDirtyFalse());
        },
        [clientSize, dispatch]
    )
    const getLidWithIntermediateCeilingSize = () =>
        getCombinedSize([getCombinedLidSize, getIntermediateCeilingSize], plan)
    const getLidWithVaultSize = () => getCombinedSize([getLidSize, plan => getVaultSize(plan, false)], plan)
    const getVisualPartSize = () => {
        return !!plan.intermediateCeiling ? getLidWithIntermediateCeilingSize()
            : plan.lid.type === 'subfloor' && !!plan.vault ? getLidWithVaultSize()
                : getLidSize(plan)
    }
    const layout = createGroundViewLayout(clientSize, plan, getVisualPartSize())
    const getHorizontalMeasurementStops = () => {
        const getInnerMeasurements = () => {
            const transformToDrawing = positions => {
                return positions.map(
                    p => ({
                        x: p.x - getOverallSize(plan).x,
                        y: p.y - getVisualPartSize().y
                    })
                )
            }

            return createMeasurementStopChainFromPositions(
                transformToDrawing(
                    getGroundViewBottomMeasurementPositions(plan)
                )
            )
        }

        return toWithOverallMeasurement(getInnerMeasurements())
    }
    const getVerticalMeasurementStops = () => {
        const lidSize = getLidSize(plan)
        const getInnerMeasurements = () => {
            const transformToDrawing = positions => {
                const getZeroXOnDrawing = () => -getOverallSize(plan).x + lidSize.x

                return positions.map(
                    p => ({
                        x: p.y - getVisualPartSize().y,
                        y: -(p.x - lidSize.x + getZeroXOnDrawing())
                    })
                )
            }

            return createMeasurementStopChainFromPositions(
                transformToDrawing(
                    getGroundViewLeftMeasurementPositions(plan)
                )
            )
        }

        return toWithOverallMeasurement(getInnerMeasurements())
    }

    // noinspection JSSuspiciousNameCombination
    return (
        <div>
            {!isNaN(layout.verticalMeasurements.screenHeight) &&
                <Stage
                    ref={theStage}
                    name={"stage"}
                    width={clientSize.width}
                    height={layout.overall.screenHeight}
                >
                    <Layer>
                        {
                            <Group x={layout.part.screenX} y={layout.part.screenY}>
                                <LidGroundView plan={plan} scale={layout.part.scale}/>
                            </Group>
                        }
                        <Group
                            x={layout.horizontalMeasurements.screenX}
                            y={layout.horizontalMeasurements.screenY}
                        >
                            {
                                createDimensioning(
                                    getHorizontalMeasurementStops(),
                                    'below',
                                    {
                                        width: layout.horizontalMeasurements.screenWidth,
                                        height: layout.horizontalMeasurements.screenHeight
                                    },
                                    layout.verticalMeasurements.scale
                                )
                            }
                        </Group>
                        <Group
                            x={layout.verticalMeasurements.screenX + layout.verticalMeasurements.screenWidth}
                            y={layout.verticalMeasurements.screenY + layout.verticalMeasurements.screenHeight}
                            rotation={-90}
                        >
                            {
                                createDimensioning(
                                    getVerticalMeasurementStops(),
                                    'above',
                                    {
                                        width: layout.verticalMeasurements.screenHeight,
                                        height: layout.verticalMeasurements.screenWidth
                                    },
                                    layout.verticalMeasurements.scale
                                )
                            }
                        </Group>
                    </Layer>
                </Stage>
            }
        </div>
    )
}

export const LidGroundViewWithMeasurements = props => {
    return (
        <AutoResizeDiv>
            <GroundView {...props} />
        </AutoResizeDiv>
    )
}
